import { createRef, useEffect, useState } from 'react';
import { Box, CircularProgress, Divider, Stack } from '@mui/material';
import { MapGoogle, MapLeaflet, MapOption, MapSearch, RegulationList } from '../component';
import { CountryService, PortService, RegionService, RegulationService, StateService, getAuthToken, SubjectListService } from '../service';
import { useMsal } from '@azure/msal-react';
import { Lookups } from '../utils';
import RegulationListUpdatedOn from '../component/regulation-updateOn/regulation-list-updatedOn.component';

import { AltanticArea, CaribbeanArea, HawaiiArea, PacificArea } from '../data';

const mapBoxStyle = {
    position: 'relative',
    width: '100%',
    height: '78vh',
    margin: '0px auto 0 auto',
    display: { xs: 'block', sm: 'block', md: 'block' }
};

const mapBoxContentStyle = {
    position: 'absolute',
    top: '12px',
    left: '12px',
    zIndex: '1000'
};

const Map2 = () => {
    // Variables for utility functions
    const { instance, accounts } = useMsal();
    const refMap = createRef();
    const refRegulation = createRef();
    const [isLoading, setIsLoading] = useState(true);

    // Variables used for map option changes
    const [mapLayer, setMapLayer] = useState();
    const [mapOptions, setMapOptions] = useState([]);

    // Variables used for map markers
    const [regionData, setRegionData] = useState([]);
    const [countryData, setCountryData] = useState([]);
    const [stateData, setStateData] = useState([]);
    const [portData, setPortData] = useState([]);
    const [portIncentivesData, setPortIncentivesData] = useState([]);

    const [fuelsCountryData, setFuelsCountryData] = useState([]);

    // Variables when a location is selected
    const [selectedPin, setSelectedPin] = useState();
    const [selectedPinOption, setSelectedPinOption] = useState();
    const [regulationData, setRegulationData] = useState();

    // Variables used for zone data
    const [zoneData, setZoneData] = useState([]);

    // Regulation
    const [subject, setSubject] = useState();
    const [subjectList, setSubjectList] = useState([]);

    // Get the Regulation Updated On data
    const [regulationSeeUpDate, setRegulationSeeUpDate] = useState();

    useEffect(() => {
        (async () => {
            setIsLoading(true);
            // Regulatory - Get data from API once if region, country, state, port, and port incentives area selected as map options or layer.
            mapOptions.includes(Lookups.MapRegulatoryGroup.Region) && regionData.length <= 0 ? await getRegionData() : [];
            mapOptions.includes(Lookups.MapRegulatoryGroup.Country) && countryData.length <= 0 ? await getCountryData() : [];
            mapOptions.includes(Lookups.MapRegulatoryGroup.State) && stateData.length <= 0 ? await getStateData() : [];
            mapOptions.includes(Lookups.MapRegulatoryGroup.Port) && portData.length <= 0 ? await getPortData() : [];

            // Incentives
            mapLayer === Lookups.MapLayer.Incentive && portIncentivesData.length <= 0 ? await getPortIncentivesData() : [];

            // Fuels
            if (mapLayer === Lookups.MapLayer.Fuels && mapOptions.includes(Lookups.MapRegulatoryGroup.Country) && fuelsCountryData.length <= 0) {
                await getFuelsCountryData();
            }

            // Area
            mapLayer === Lookups.MapLayer.Area && zoneData.length <= 0 ? await getZoneData() : [];

            setIsLoading(false);
        })();
    }, [mapLayer, mapOptions]);

    useEffect(() => {
        (async () => {
            if (selectedPin && selectedPinOption) {
                setIsLoading(true);
                await getRegulationData();
                setIsLoading(false);
            }
        })();
    }, [selectedPin, selectedPinOption]);

    useEffect(() => {
        if ((!regulationData && !regulationSeeUpDate) || selectedPin || selectedPinOption) return;

        refRegulation.current.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
        });

        window.setTimeout(() => {
            if ((!regulationData && !regulationSeeUpDate) || !refRegulation.current) return;

            refRegulation.current.scrollIntoView({
                behavior: 'smooth',
                block: 'start'
            });
        }, 1000);
    }, [regulationData, regulationSeeUpDate]);

    const getZoneData = async () => {
        setZoneData([AltanticArea, CaribbeanArea, HawaiiArea, PacificArea]);
    };

    const getRegionData = async () => {
        const authToken = await getAuthToken(instance, accounts);
        const data = await RegionService.getAll(authToken);

        if (!data) {
            console.error('Error : Getting Region Data.');
            return;
        }

        setRegionData(data);
        setRegulationSeeUpDate();
    };

    const getCountryData = async () => {
        const authToken = await getAuthToken(instance, accounts);
        const data = await CountryService.getAll(authToken);

        if (!data) {
            console.error('Error : Getting Country Data.');
            return;
        }

        setCountryData(data);
        setRegulationSeeUpDate();
    };

    const getStateData = async () => {
        const authToken = await getAuthToken(instance, accounts);
        const data = await StateService.getAll(authToken);

        if (!data) {
            console.error('Error : Getting State Data.');
            return;
        }

        setStateData(data);
        setRegulationSeeUpDate();
    };

    const getPortData = async () => {
        const authToken = await getAuthToken(instance, accounts);
        const data = await PortService.getAll(authToken);

        if (!data) {
            console.error('Error : Getting Port Data.');
            return;
        }

        setPortData(data);
        setRegulationSeeUpDate();
    };

    const getPortIncentivesData = async () => {
        const authToken = await getAuthToken(instance, accounts);
        const data = await PortService.getAllIncentives(authToken);

        if (!data) {
            console.error('Error : Getting Port Incentives Data.');
            return;
        }

        setPortIncentivesData(data);
        setRegulationSeeUpDate();
    };

    const getRegulationData = async () => {
        const authToken = await getAuthToken(instance, accounts);

        // Map location type to API Lookup
        const locationType = getMatchData(mapLayer, selectedPinOption);

        if (!locationType) {
            console.error("Error : Can't get match data for location type.");
            return;
        }

        const data = await RegulationService.getDetail(authToken, selectedPin._id, locationType);

        if (!data) {
            console.error('Error : Getting Regulation Data.');
            return;
        }

        // Get subject list for selected location
        const subjectListData = await SubjectListService.getSubjectList(authToken, selectedPin._id, locationType);

        if (!subjectListData) {
            console.error('Error : Getting Regulation Subject.');
            return;
        }

        setSubjectList(subjectListData);
        setRegulationData(data);
        setRegulationSeeUpDate();
    };

    const getMatchData = (layer, lookup) => {
        if (layer === Lookups.MapLayer.Incentive || selectedPinOption === Lookups.MapRegulatoryGroup.Port) {
            return 'Port';
        } else if (selectedPinOption === Lookups.MapRegulatoryGroup.Region) return 'Region';
        else if (selectedPinOption === Lookups.MapRegulatoryGroup.Country) return 'Country';
        else if (selectedPinOption === Lookups.MapRegulatoryGroup.State) return 'State';
    };

    const getFuelsCountryData = async () => {
        const authToken = await getAuthToken(instance, accounts);
        const data = await CountryService.getAllFuels(authToken);

        if (!data) {
            console.error('Error : Getting Country Fuels Data.');
            return;
        }

        setFuelsCountryData(data);
        setRegulationSeeUpDate();
    };

    const handleMapSelectionChange = (layer, options) => {
        setMapLayer(layer);
        setMapOptions(options);
        setSubject();
        setRegulationSeeUpDate();
    };

    const handleWorldWideMapClick = async () => {
        // Reset Pin and Subject
        setSubject();
        setSelectedPin();
        setSelectedPinOption();
        setRegulationSeeUpDate();

        // Get all regulation data
        const authToken = await getAuthToken(instance, accounts);
        var data = await RegulationService.getWorldWide(authToken);

        if (!data) {
            console.error('Error: Getting WorldWide data.');
            return;
        }

        // Get subject list for world wide
        const subjectListData = await SubjectListService.getSubjectListWorldWide(authToken);

        if (!subjectListData) {
            console.error('Error : Getting Regulation Subject.');
            return;
        }

        setSubjectList(subjectListData);
        setRegulationData(data);
    };

    const handleAreaOptionClick = async (option) => {
        setSubject({ name: option });

        // Reset Pin if selected
        setSelectedPin();
        setSelectedPinOption();
        setRegulationSeeUpDate();

        // Get all regulation data
        const authToken = await getAuthToken(instance, accounts);
        var data = await RegulationService.getWorldWide(authToken);

        if (!data) {
            console.error('Error: Getting WorldWide data.');
            return;
        }

        setRegulationData(data);
    };

    const handlePinSelectClick = (pin, option) => {
        if (!pin || !option) {
            setSelectedPin();
            setSelectedPinOption();
            setRegulationData();
            setRegulationSeeUpDate();

            return;
        }

        setSubject();
        setSelectedPin(pin);
        setSelectedPinOption(option);
        setRegulationSeeUpDate();
    };

    const handleSeeMoreClick = () => {
        if (regulationData) {
            refRegulation.current.scrollIntoView({
                behavior: 'smooth',
                block: 'start'
            });
        }
    };

    const handleSeeUpdateClick = async () => {
        // Reset Pin and Subject
        setSubject();
        setSelectedPin();
        setSelectedPinOption();
        setRegulationData();

        // Get all regulation data
        const authToken = await getAuthToken(instance, accounts);
        const data = await RegulationService.getSeeUpdate(authToken, 3);
        if (!data) {
            console.error('Error: Getting WorldWide data.');
            return;
        }

        setRegulationSeeUpDate(data);
    };

    const getRegionLocation = () => {
        // Logic when to show or hide data on the map.
        return mapLayer === Lookups.MapLayer.Regulatory ? regionData : [];
    };

    const getCountryLocation = () => {
        // Logic when to show or hide data on the map.
        return mapLayer === Lookups.MapLayer.Regulatory ? countryData : [];
    };

    const getStateLocation = () => {
        // Logic when to show or hide data on the map.
        return mapLayer === Lookups.MapLayer.Regulatory ? stateData : [];
    };

    const getPortLocation = () => {
        // Logic when to show or hide data on the map.
        return mapLayer === Lookups.MapLayer.Regulatory ? portData : [];
    };

    const getPortIncentivesLocation = () => {
        // Logic when to show or hide data on the map.
        return mapLayer === Lookups.MapLayer.Incentive ? portIncentivesData : [];
    };

    const getCountryFuelsLocation = () => {
        // Logic when to show or hide data on the map.
        return mapLayer === Lookups.MapLayer.Fuels ? fuelsCountryData : [];
    };

    const handleChipDelete = () => {
        setSelectedPin();
        setSelectedPinOption();
        setRegulationData();
        setRegulationSeeUpDate();

        refMap.current.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
        });
    };

    const getChipLabel = () => {
        let label;

        if (selectedPin && selectedPinOption) {
            if (selectedPinOption === Lookups.MapRegulatoryGroup.Region) label = 'Region :';
            else if (selectedPinOption === Lookups.MapRegulatoryGroup.Country) label = 'Country :';
            else if (selectedPinOption === Lookups.MapRegulatoryGroup.State) label = 'State :';
            else if (selectedPinOption === Lookups.MapRegulatoryGroup.Port) label = 'Port :';

            return `${label} ${selectedPin.name}`;
        }

        if (mapLayer === Lookups.MapLayer.Area && subject) {
            return `Area : ${subject.name}`;
        }

        // Return empty when no pin is selected
        return 'Worldwide';
    };

    return (
        <>
            {/* Map */}
            <Stack spacing={2} direction={'row'} ref={refMap}>
                <Box sx={mapBoxStyle}>
                    <Stack direction={'row'} spacing={1} sx={mapBoxContentStyle}>
                        <MapSearch OnPinSelect={handlePinSelectClick} selectedPin={selectedPin} selectedPinOption={selectedPinOption} />
                        <MapOption
                            OnSelectionChange={handleMapSelectionChange}
                            OnWorldWideClick={handleWorldWideMapClick}
                            OnAreaOptionClick={handleAreaOptionClick}
                            OnSeeUpdateClick={handleSeeUpdateClick}
                        />
                    </Stack>

                    {mapLayer === Lookups.MapLayer.Area ? (
                        // Use Leaflet Map for Area Layer
                        <MapLeaflet zoneData={zoneData} />
                    ) : (
                        <MapGoogle
                            OnPinSelect={handlePinSelectClick}
                            OnSeeMore={handleSeeMoreClick}
                            mapLayer={mapLayer}
                            mapOptions={mapOptions}
                            regionData={getRegionLocation()}
                            countryData={getCountryLocation()}
                            stateData={getStateLocation()}
                            portData={getPortLocation()}
                            portIncentivesData={getPortIncentivesLocation()}
                            countryFuelsData={getCountryFuelsLocation()}
                            selectedPin={selectedPin}
                            selectedPinOption={selectedPinOption}
                        />
                    )}
                </Box>
            </Stack>

            {/* Regulation List */}
            <Stack spacing={1} direction={'column'} ref={refRegulation}>
                {isLoading && (
                    <Box sx={{ textAlign: 'center', marginTop: '32px', minHeight: '500px' }}>
                        <CircularProgress />
                    </Box>
                )}
                {!isLoading && regulationData && (
                    <>
                        <Divider variant="middle" color="#F5F7F8" sx={{ borderBottomWidth: 2, marginTop: '12px' }} />
                        <RegulationList regulationData={regulationData} chipLabel={getChipLabel()} onChipDelete={handleChipDelete} regulationSubject={subject} subjectListSelect={subjectList} />
                    </>
                )}

                {!isLoading && regulationSeeUpDate && (
                    <>
                        <Divider variant="middle" color="#F5F7F8" sx={{ borderBottomWidth: 2, marginTop: '12px' }} />
                        <RegulationListUpdatedOn regulationSeeUpDate={regulationSeeUpDate} chipLabel={'Recent Updates'} onChipDelete={handleChipDelete} />
                    </>
                )}
            </Stack>
        </>
    );
};

export default Map2;
